/**
 * Generic GenAI streaming client for all AI-powered features.
 * Uses Server-Sent Events for real-time streaming from Bedrock.
 */

import {
  getAuthHeaders, API_BASE_URL 
} from './api-client';

type ImageMediaType = 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';

export interface GenAIRequest {
  promptKey: string;
  userPrompt: string;
  context?: Record<string, unknown>;
  image?: {
    base64: string;
    mediaType: ImageMediaType;
  };
}

export type StreamCallback = (chunk: string) => void;
export type CompleteCallback<T> = (result: T) => void;
export type ErrorCallback = (error: Error) => void;

interface SSEMessage {
  chunk?: string;
  done?: boolean;
  error?: string;
}

function parseSSEMessage(data: string): SSEMessage | null {
  try {
    return JSON.parse(data) as SSEMessage;
  } catch {
    return null;
  }
}

function processSSELine(
  line: string,
  state: { fullResponse: string },
  onChunk: StreamCallback
): SSEMessage | null {
  if (!line.startsWith('data: ')) {
    return null;
  }

  const data = line.slice(6);
  const parsed = parseSSEMessage(data);

  if (!parsed) {
    return null;
  }

  if (parsed.error) {
    throw new GenAIError(500, parsed.error);
  }

  if (parsed.chunk) {
    state.fullResponse += parsed.chunk;
    onChunk(parsed.chunk);
  }

  return parsed;
}

function processSSEBuffer(
  buffer: string,
  state: { fullResponse: string },
  onChunk: StreamCallback
): {
    remaining: string;
    isDone: boolean 
  } {
  const lines = buffer.split('\n');
  const remaining = lines.pop() ?? '';

  for (const line of lines) {
    const message = processSSELine(line, state, onChunk);
    if (message?.done) {
      return {
        remaining,
        isDone: true 
      };
    }
  }

  return {
    remaining,
    isDone: false 
  };
}

/**
 * Stream a GenAI response from Bedrock.
 * @param request - The request configuration including promptKey and user input
 * @param onChunk - Called for each streamed chunk
 * @param onComplete - Called when streaming completes with parsed result
 * @param onError - Called on error
 * @param signal - Optional AbortSignal for cancellation
 */
export async function streamGenAI<T>(
  request: GenAIRequest,
  onChunk: StreamCallback,
  onComplete: CompleteCallback<T>,
  onError: ErrorCallback,
  signal?: AbortSignal
): Promise<void> {
  const url = `${API_BASE_URL}/v1/genai/stream`;

  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...authHeaders,
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      throw new GenAIError(
        response.status,
        `GenAI request failed: ${response.statusText}`
      );
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new GenAIError(500, 'No response body');
    }

    const decoder = new TextDecoder();
    const state = { fullResponse: '' };
    const bufferState = { value: '' };

    while (true) {
      const readResult = await reader.read();

      if (readResult.done) break;

      bufferState.value += decoder.decode(readResult.value, { stream: true });
      const processResult = processSSEBuffer(bufferState.value, state, onChunk);
      bufferState.value = processResult.remaining;

      if (processResult.isDone) {
        const result = parseJsonResponse<T>(state.fullResponse);
        onComplete(result);
        return;
      }
    }

    // If we get here without a done signal, try to parse what we have
    if (state.fullResponse) {
      const result = parseJsonResponse<T>(state.fullResponse);
      onComplete(result);
    }
  } catch (error) {
    handleStreamError(error, onError);
  }
}

function handleStreamError(error: unknown, onError: ErrorCallback): void {
  if (error instanceof GenAIError) {
    onError(error);
    return;
  }

  if (error instanceof Error) {
    const errorToReport = error.name === 'AbortError'
      ? new GenAIError(0, 'Request cancelled')
      : new GenAIError(500, error.message);
    onError(errorToReport);
    return;
  }

  onError(new GenAIError(500, 'Unknown error'));
}

/**
 * Convert an image file to base64 for multimodal requests.
 */
export async function imageToBase64(
  file: File
): Promise<{
    base64: string;
    mediaType: ImageMediaType 
  }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      const mediaType = file.type as ImageMediaType;
      resolve({
        base64,
        mediaType,
      });
    };
    reader.onerror = () => reject(new GenAIError(500, 'Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

function parseJsonResponse<T>(jsonString: string): T {
  const cleaned = cleanJsonString(jsonString);

  try {
    return JSON.parse(cleaned) as T;
  } catch {
    throw new GenAIError(500, 'Failed to parse JSON from AI response');
  }
}

function cleanJsonString(jsonString: string): string {
  const trimmed = jsonString.trim();

  const withoutJsonPrefix = trimmed.startsWith('```json')
    ? trimmed.slice(7)
    : trimmed;

  const withoutPrefix = withoutJsonPrefix.startsWith('```')
    ? withoutJsonPrefix.slice(3)
    : withoutJsonPrefix;

  const withoutSuffix = withoutPrefix.endsWith('```')
    ? withoutPrefix.slice(0, -3)
    : withoutPrefix;

  return withoutSuffix.trim();
}

export class GenAIError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string
  ) {
    super(message);
    this.name = 'GenAIError';
  }
}
